// NOTE: these are providers ids in simply book

import { runtimeConfig } from '@app/common/config'
import { AbTestVariants } from '@app/store/ui'

// if some user stop to work with us - we remove his/her id from this list
export const PROVIDERS = '2,3,5,6,7,8,9,10'

// List of valid campaigns. Current campaign comes from .env/AB_TEST_IS_EXECUTING and validated against this list. See fillStoreInitBookingFlow.ts.
// Set .env/AB_TEST_IS_EXECUTING to zero to disable A/B testing.
export const AB_TEST_CAMPAIGNS = ['1']

// When list of available variants is changed, campaign number must be increased (in .env and also added to AB_TEST_CAMPAIGNS)
// (However, there is validation in fillStoreInitBookingFlow.ts, that if the variant coming from the cookie is not here, then a new random variant is picked)
// Chosen randomly in fillStoreInitBookingFlow.ts
export const AB_TEST_VARIANTS_BY_CAMPAIGN: {
  readonly [campaignId: string]: AbTestVariants[]
} = {
  1: [AbTestVariants.Control, AbTestVariants.Test1]
}

// if set, always this variant is used instead of random selection (regardless of AB_TEST_VARIANTS_BY_CAMPAIGN
// this is for development testing purposes
export const AB_TEST_VARIANT_ENFORCED: AbTestVariants | undefined = undefined

// Explanation of campaigns vs variants
// Imageine, you are first time user
// 1) open the browser, AB_TEST_IS_EXECUTING = 1 (campaign)
// 2) app looks for cookie abTest_1
// 3) cannot find it, so generates a variant number randomly what is available that time....so it picks Original or Test1...let's say it picked Original
// 4) it stores (empty) to abTest_1 cookie
// 5) next time you open the browser, AB_TEST_IS_EXECUTING = 1 still
// 6) it looks for the cookie abTest_1 and finds that the Original variant was chosen for you
// 7) time passes, Elemy decides to start new campaign, sets AB_TEST_IS_EXECUTING = 2 and sets the available variants to Original and Test2
// 8) you open the browser, app looks for cookie abTest_2 (campaign)
// 9) it's not found so it generates a variant number randomly, either Original or Test2
// 10) etc.

export const VALIDATION_MESSAGES = {
  required: '{fieldName} is a required field'
}

export enum Level {
  LevelRbtTraining = 'LEVEL_RBT_TRAINING',
  LevelRbt = 'LEVEL_RBT',
  LevelBcba = 'LEVEL_BCBA',
  LevelPediatricPsychiatrist = 'LEVEL_PEDIATRIC_PSYCHIATRIST',
  LevelPsychologist = 'LEVEL_PSYCHOLOGIST',
  LevelPMHNP = 'LEVEL_PMHNP',
  LevelLCSW = 'LEVEL_LCSW',
  LevelLPC = 'LEVEL_LPC',
  LevelOther = 'LEVEL_OTHER',
  LevelBtToRbt = 'LEVEL_BT_TO_RBT'
}

export const LEVEL_NAMES: Record<Level, string> = {
  [Level.LevelRbt]: "I'm a credentialed RBT",
  [Level.LevelBcba]: "I'm a credentialed BCBA",
  [Level.LevelPediatricPsychiatrist]: "I'm a Pediatric Psychiatrist",
  [Level.LevelPsychologist]: "I'm a Psychologist",
  [Level.LevelPMHNP]: "I'm a Psychiatric Mental Health Nurse Practitioner (PMHNP)",
  [Level.LevelLCSW]: "I'm a Licensed Clinical Social Worker (LCSW)",
  [Level.LevelLPC]: "I'm a Licensed Professional Counselor (LPC)",
  [Level.LevelRbtTraining]: 'RBT Training Program (eCademy)',
  [Level.LevelOther]: 'Other',
  [Level.LevelBtToRbt]: "I'm a BT looking to become a RBT"
}

export const SMARTSHEET_LEVELS = [
  Level.LevelPediatricPsychiatrist,
  Level.LevelPsychologist,
  Level.LevelPMHNP,
  Level.LevelLCSW,
  Level.LevelLPC,
  Level.LevelRbtTraining,
  Level.LevelOther
]

export const SMARTSHEET_LEVELS_MAP = {
  [Level.LevelPediatricPsychiatrist]: 'Pediatric Psychiatrist (MD/DO)',
  [Level.LevelPsychologist]: 'Psychologist (PsyD)',
  [Level.LevelPMHNP]: 'Psychiatric Mental Health Nurse Practitioner (PMHNP)',
  [Level.LevelLCSW]: 'Licensed Clinical Social Worker (LCSW)',
  [Level.LevelLPC]: 'Licensed Professional Counselor (LPC)',
  [Level.LevelRbtTraining]: 'RBT Training Program (eCademy)',
  [Level.LevelOther]: 'Other, please describe'
}

export const LEVEL_OPTIONS = Object.values(Level).map(key => ({
  value: key,
  label: LEVEL_NAMES[key]
}))

const M_CHAT_RESULTS_ENDPOINT = runtimeConfig.joinSproutTherapyConfig?.M_CHAT_RESULTS_ENDPOINT

export const ROUTES = {
  homePage: '/',
  autismCare: '/services/aba-therapy',
  autismDiagnosis: '/services/autism-diagnosis',
  pediatricPsychiatryDiagnostics: '/services/pediatric-psychiatry-diagnostics',
  personalizedTherapy: '/services/personalized-therapy',
  medicationManagement: '/services/medication-management',
  intensiveOutpatientProgram: '/services/intensive-outpatient-program',
  locations: '/locations',
  selectedState: '/locations/states/:state',
  selectedCity: '/locations/cities/:city',
  ourClinicians: '/team',
  learningStudio: '/studio',
  learningStudioCategory: '/studio/:category',
  learningStudioArticle: '/studio/:category/:article',
  faq: '/faq',
  joinUs: '/join-us',
  rbtSignUp: '/sign-up/rbt',
  corporateCareers: '/corporate-careers',
  forPediatricians: '/for-physicians',
  openRoles: '/open-roles',
  ourStory: '/our-story',
  contactUs: '/contact',
  mchat: M_CHAT_RESULTS_ENDPOINT ? '/mchat' : '/page404',
  abaCostCalculator: '/aba-cost-calculator',
  booking: '/parent-booking',
  terms: '/terms',
  policy: '/policy',
  nonDiscrimination: '/notice-of-non-discrimination',
  privacyPractices: '/privacy-practices',
  press: '/press',
  sitemap: '/sitemap.xml',
  page404: 'page404'
}

export const BOOKING_INIT_ROUTE = ROUTES.booking

export const GREENHOUSE_PROXY_URL = `/v1/boards/${runtimeConfig.GREENHOUSE_ID}/departments`

export const OLD_BLOG_ENTRIES = [
  '/aba-therapy*',
  '/autism-treatment*',
  '/autism*',
  '/autism-causes*',
  '/autism-diagnosis*',
  '/aba-terms*',
  '/speech-therapy*',
  '/aba-therapists*',
  '/behavior-analysts*',
  '/rbt*',
  '/autism-therapy*',
  '/autism-and-education*',
  '/autism-resources*',
  '/occupational-therapy*',
  '/types-of-autism*',
  '/autism-family-guide*',
  '/autism-and-diet*',
  '/autism-symptoms*',
  '/mood-disorders*',
  '/aspergers*',
  '/guide*',
  '/guides*',
  '/preventive*',
  '/career*'
]

export const ARTICLES_WITH_SPACE = [
  '/studio/%20autism-treatment/best-options-in-2020',
  '/studio/%20autism-treatment/pecs',
  '/studio/%20autism-treatment/marijuana-research',
  '/studio/%20autism-and-education/homeschooling-pros-and-cons',
  '/studio/%20autism-and-education/colleges-for-autistic-students',
  '/studio/%20autism-treatment/tms',
  '/studio/%20autism-diagnosis/testing-for-adults',
  '/studio/%20aba-terms/forward-chaining',
  '/studio/%20aba-terms/overcorrection',
  '/studio/%20aspergers/common-traits',
  '/studio/%20autism-therapy/at-home',
  '/studio/%20autism-diagnosis/testing-for-adults',
  '/studio/%20mood-disorders/autism-and-epilepsy',
  '/studio/%20mood-disorders/autism-and-speech-apraxia'
]

export const TRANSLATIONS = {
  apiErrors: {},
  genericError: 'Something went wrong. Please reload app and try again',
  // CRITICAL! Do not change this number, as CallTrackingMetrics script relies on it.
  // Contact marketing team to confirm the changes of the CTM before changing!
  genericPhoneNum: '(833) 458-0386'
}

// we use this domain for our own landing pages. Using https://app.unbounce.com service
export const MARKETING_ONBOUNCE_LANDING_PAGE_DOMAIN = 'autism.elemy.com'
export const MARKETING_ATTRIBUTION_COOKIE = 'marketing_attribution'
export const MARKETING_ATTRIBUTION_COOKIE_EXPIRATION_TIME = 90 * 24 * 60 * 60 * 1000 // 90 days
export const LANDING_PAGE_COOKIE = 'landing_page_url'
export const FIRST_TOUCH_URL_COOKIE = 'first_touch_url'
export const OBSOLETE_CURRENT_BOOKING_FLOW_STEP = 'booking_flow_step'
export const CURRENT_MCHAT_STEP = 'mchat_step'
export const SESSION_ID_COOKIE = 'user_session_id'
export const SESSION_ID_COOKIE_EXPIRATION_TIME = 7 * 24 * 60 * 60 * 1000 // 7 days
export const BOOKING_FLOW_USER_ID_COOKIE = 'booking_flow_user_id'
export const BOOKING_FLOW_USER_ID_COOKIE_EXPIRATION_DAYS = 90
export const OBSOLETE_AB_TEST_VARIANT_COOKIE = 'user_variant' // obsolete, will be removed if encountered (new: AB_TEST_
export const AB_TEST_DATA_VARIANT_COOKIE = 'ab_test_' // keyyed by campaign index, returns variant
export const AB_TEST_DATA_STEP_COOKIE = 'ab_test_step_' // keyyed by variant index, returns step
export const URL_REFLECTION_COOKIE = 'url_reflection'
export const URL_REFLECTION_COOKIE_EXPIRATION_TIME = 10 * 60 * 1000 // 10 mins
export const LD_SSR_FLAGS = 'ld_ssr_flags'
export const LD_SSR_FLAGS_EXPIRATION_TIME = 24 * 60 * 60 * 1000 // 24h

export enum LeadSources {
  Facebook = 'Facebook',
  FacebookPaid = 'Facebook Paid',
  GoogleAds = 'Google Ads',
  GoogleOrganic = 'Google Organic',
  GoogleMyBusiness = 'Google My Business', // LEAD_SOURCE_GOOGLE_MAPS
  YextPartners = 'Yext Partners', // LEAD_SOURCE_GOOGLE_MAPS
  GoogleMaps = 'Google Maps', // LEAD_SOURCE_GOOGLE_MAPS
  BingOrganic = 'Bing Organic',
  BingPaid = 'Bing Paid',
  Direct = 'Direct',
  Other = 'Other'
}

export const NATIONAL_PHONE_NUMBER = '(833) 458-0386'
export const NATIONAL_PHONE_NUMBER_URL = 'tel: +18334580386'

export const DIAGNOSIS_SUPPORTED_STATES = ['FL', 'TX']
export const DIAGNOSTIC_SERVICES_URL = 'https://autism.elemy.com/diagnostic-bookingflow/'
